import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { Link } from "react-router-dom";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GpsFixedOutlinedIcon from "@mui/icons-material/GpsFixedOutlined";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function PrivacyPolicy() {
  const lastUpdated = "August 15, 2026";

  const sections = [
    {
      id: "intro",
      icon: <ShieldOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "1. Introduction & Overview",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            Welcome to <strong>RideXCity</strong> ("Platform", "We", "Our", "Us"), a brand owned and operated by <strong>VALUEAID INFRASTRUCTURE PRIVATE LIMITED</strong>, having its registered and operational office at 2nd Floor, 9, Outer Ring Rd, HBR Layout 5th Block, Banashankari 3rd Stage, Hosakerehalli, Bengaluru, Karnataka 560085.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            RideXCity is dedicated to providing sustainable, high-performance electric vehicle (EV) rentals, battery swapping, smart mobility telematics, and urban commute solutions. We value your trust and are committed to safeguarding the privacy and security of your personal data.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
            This Privacy Policy explains how we collect, process, store, disclose, and protect your information when you visit our website (<strong>ridexcity.com</strong>), use our mobile applications, rent our electric vehicles, access battery swapping stations, or interact with our customer support.
          </Typography>
        </>
      ),
    },
    {
      id: "collection",
      icon: <PermIdentityOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "2. Information We Collect",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            We collect information required to verify your eligibility, provision EV rental services, ensure road safety, and comply with statutory legal mandates under Indian law:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>
              <strong>Personal & Identity Information:</strong> Full name, mobile phone number, email address, date of birth, gender, residential address, and emergency contact details.
            </li>
            <li>
              <strong>KYC & Verification Documents:</strong> Government-issued identity proofs (Aadhaar Card, Voter ID, PAN card), valid Driving License (where mandatory for vehicle category under RTO rules), and a real-time selfie/photograph for biometric identity confirmation.
            </li>
            <li>
              <strong>Vehicle Telematics & Location Data:</strong> Continuous real-time GPS coordinates of the EV, trip routes, speed, acceleration/braking metrics, battery status, distance travelled, lock/unlock events, and geofencing compliance records.
            </li>
            <li>
              <strong>Financial & Transaction Data:</strong> Rental plan payments, security deposits, refund requests, digital wallet transactions, invoice records, and billing details processed securely via RBI-authorized, PCI-DSS compliant payment gateways.
            </li>
            <li>
              <strong>Device & Technical Diagnostics:</strong> Device model, OS version, IP address, unique device identifiers, network operator, app crash logs, and performance metrics.
            </li>
          </Box>
        </>
      ),
    },
    {
      id: "usage",
      icon: <GpsFixedOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "3. How We Use Your Information",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            The data collected is utilized solely for operational, safety, and legal purposes, including:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>Verifying identity, validating driving credentials, and approving rider onboarding.</li>
            <li>Enabling smart app-based EV locking/unlocking, battery swap authentication, and hub check-in/check-out.</li>
            <li>Real-time vehicle safety monitoring, theft prevention, roadside assistance dispatch, and geo-zone compliance.</li>
            <li>Processing rental subscriptions, deposit management, toll/challan deductions, and refund settlements.</li>
            <li>Sending operational updates, ride receipts, safety advisories, and critical service notifications via SMS, WhatsApp, and email.</li>
            <li>Complying with the Motor Vehicles Act, 1988, law enforcement inquiries, and statutory audit obligations.</li>
          </Box>
        </>
      ),
    },
    {
      id: "sharing",
      icon: <LockOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "4. Information Sharing & Third-Party Disclosures",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            <strong>We do not sell, rent, or trade your personal information to third-party marketing companies.</strong> We only share data with trusted entities under strict confidentiality agreements:
          </Typography>
          <Box component="ul" sx={{ pl: 3, color: "text.secondary", lineHeight: 1.9 }}>
            <li>
              <strong>Insurance Providers:</strong> In case of an accident, collision, or vehicle damage, necessary trip details and KYC documents are submitted to process insurance claims.
            </li>
            <li>
              <strong>Payment Processors & Banks:</strong> To facilitate seamless digital transactions, UPI auto-debits, and refund processing.
            </li>
            <li>
              <strong>Law Enforcement & Government Authorities:</strong> When required by lawful summons, court orders, investigation of traffic offenses, or stolen vehicle recovery.
            </li>
            <li>
              <strong>B2B Delivery & Fleet Partners:</strong> If you are onboarding as an enterprise/gig delivery partner, essential vehicle usage and attendance logs may be coordinated with your assigned fleet manager.
            </li>
          </Box>
        </>
      ),
    },
    {
      id: "security",
      icon: <PaymentsOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "5. Data Security, Retention & User Rights",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            We implement enterprise-grade technical and organizational safeguards (256-bit SSL encryption, restricted access control, tokenized payment storage) to prevent unauthorized access, alteration, or loss of your personal information.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            <strong>Retention Period:</strong> We retain your personal data and telemetry records for as long as your account is active, or as mandated by applicable Indian financial, taxation, and motor vehicle regulations.
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
            <strong>Your Rights:</strong> Under the Digital Personal Data Protection (DPDP) Act and applicable Indian laws, you have the right to request access, correction, or deletion of your personal data, subject to ongoing rental liabilities, pending dues, or legal retention obligations.
          </Typography>
        </>
      ),
    },
    {
      id: "grievance",
      icon: <SupportAgentOutlinedIcon color="primary" sx={{ fontSize: 24 }} />,
      title: "6. Grievance Redressal & Contact Officer",
      content: (
        <>
          <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}>
            In accordance with the Information Technology Act, 2000, the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, and the Digital Personal Data Protection Act, the details of our designated Grievance Officer are:
          </Typography>
          <Card variant="outlined" sx={{ bgcolor: "background.default", borderColor: "divider", borderRadius: 3, mt: 2 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
                Grievance Redressal Officer
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
                <strong>Company:</strong> VALUEAID INFRASTRUCTURE PRIVATE LIMITED (RideXCity)
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
                <strong>Address:</strong> 2nd Floor, 9, Outer Ring Rd, HBR Layout 5th Block, Banashankari 3rd Stage, Hosakerehalli, Bengaluru, Karnataka 560085, India
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
                <strong>Email:</strong> <MuiLink href="mailto:support@ridexcity.com" color="primary">support@ridexcity.com</MuiLink>
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                <strong>Helpline:</strong> <MuiLink href="tel:+918123198675" color="primary">+91 81231 98675</MuiLink> (Mon - Sat, 9:30 AM - 6:30 PM IST)
              </Typography>
            </CardContent>
          </Card>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ bgcolor: "#FAFBFC", py: { xs: 4, md: 8 }, minHeight: "80vh" }}>
      <Container maxWidth="lg">
        {/* Navigation Breadcrumb */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <MuiLink
            component={Link}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 14,
              fontWeight: 600,
              color: "text.secondary",
              textDecoration: "none",
              "&:hover": { color: "primary.main" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to Home
          </MuiLink>
        </Stack>

        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3, fontSize: 13 }}>
          <MuiLink component={Link} to="/" underline="hover" color="inherit">
            Home
          </MuiLink>
          <Typography color="text.primary" sx={{ fontSize: 13, fontWeight: 600 }}>
            Privacy Policy
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            mb: 5,
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(27, 78, 216, 0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: -30,
              bottom: -30,
              width: 180,
              height: 180,
              bgcolor: "rgba(255,255,255,0.06)",
              borderRadius: "50%",
              pointerEvents: "none",
            }}
          />
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 2,
              fontWeight: 700,
              bgcolor: "rgba(255, 255, 255, 0.2)",
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              display: "inline-block",
              mb: 2,
            }}
          >
            LEGAL & COMPLIANCE
          </Typography>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: 28, md: 40 }, mb: 1.5 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 700, fontSize: { xs: 14.5, md: 16 } }}>
            Learn how RideXCity (VALUEAID INFRASTRUCTURE PRIVATE LIMITED) protects your personal information, telematics data, and KYC documentation while you ride clean and green.
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 2.5, opacity: 0.75, fontWeight: 500 }}>
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        {/* Policy Content Sections */}
        <Stack spacing={4}>
          {sections.map((sec) => (
            <Card
              key={sec.id}
              id={sec.id}
              sx={{
                borderRadius: 3,
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                  {sec.icon}
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700, fontSize: { xs: 18, md: 21 }, color: "text.primary" }}>
                    {sec.title}
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 3 }} />
                {sec.content}
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
